// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/
import * as Sentry from "@sentry/nextjs"

const FULL_RATE_SAMPLED_ERROR_CONTEXTS = new Set([
  "client_error_component", // Fallback context for route boundaries, should not be hit
  "screen_error_boundary", // /screen/* route boundaries
  "plp_error_boundary", // /product-list-page/* route errors
  "global_error_boundary", // Global layout errors
  "root_error_boundary", // Root layout errors
  "unhandled_client_error", // React fatal errors that bypass error boundaries
])

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  // sampleRate is set to 1 so that beforeSend controls sampling.
  // Error boundary events are always sent; everything else is sampled at 1%.
  sampleRate: 1,
  tracesSampleRate: process.env.NODE_ENV !== "development" ? 0.01 : 0.1,
  debug: false,
  replaysOnErrorSampleRate: 1,
  replaysSessionSampleRate: 0.1,
  environment: process.env.NODE_ENV,
  integrations: [
    Sentry.browserProfilingIntegration(),
    Sentry.browserTracingIntegration({
      enableInp: true,
      instrumentNavigation: true,
    }),
    Sentry.httpClientIntegration(),
    Sentry.extraErrorDataIntegration(),
  ],
  tracePropagationTargets: [
    /^https?:\/\/([^\/]*\.)?tapcart\.studio/,
    /^https?:\/\/([^\/]*\.)?tapc\.studio/,
  ],
  beforeSend(event) {
    // Enrich all client error events with appId and layoutId from the URL
    if (typeof window !== "undefined") {
      const segments = window.location.pathname.split("/")
      if (segments[1]) event.tags = { ...event.tags, appId: segments[1] }
      if (segments[3]) event.tags = { ...event.tags, layoutId: segments[3] }
    }

    /* --------------------------------------------------------------
       Commenting out the full rate sampled error contexts for now
       keeping this here as a reference for future use if we want to increase
       sampling on specific error contexts to help debug issues.
    
      const context = event.tags?.context as string | undefined
      // Always send error boundary events and unhandled client errors
      if (context && FULL_RATE_SAMPLED_ERROR_CONTEXTS.has(context)) {
        return event
      }
      // Always send unhandled errors (window.onerror / unhandledrejection)
      // These include React fatal errors that bypass error boundaries.
      // Exclude `http.client` — synthetic events from httpClientIntegration are
      // not real unhandled exceptions and should go through the 1% sample.
      if (
        event.exception?.values?.some(
          (v) =>
            v.mechanism?.handled === false && v.mechanism?.type !== "http.client"
        )
      ) {
        event.tags = { ...event.tags, context: "unhandled_client_error" }
        return event
      }
    -------------------------------------------------------------- */
    // Sample everything at 1%
    if (process.env.NODE_ENV !== "development" && Math.random() > 0.01) {
      return null
    }
    return event
  },
})
